import React, { useEffect, useState } from 'react';
import { Article } from '@/types/article';
import { parseMarkdown } from '@/utils/markdownUtils';

interface ArticleContentProps {
  article: Article;
}

const ArticleContent: React.FC<ArticleContentProps> = ({ article }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const renderMarkdown = async () => {
      try {
        setIsLoading(true);
        const parsedHtml = await parseMarkdown(article.content);
        setHtmlContent(parsedHtml);
      } catch (error) {
        console.error('Error parsing markdown:', error);
      } finally {
        setIsLoading(false);
      }
    };

    renderMarkdown();
  }, [article.content]);

  return (
    <article className="prose prose-lg max-w-none">
      <div className="mb-8">
        <p className="text-xl text-gray-600 leading-relaxed">{article.excerpt}</p>
      </div>
      
      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      ) : (
        <div 
          className="article-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}

      {(article.tags ?? []).length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {(article.tags ?? []).map((tag, i) => {
              if (!tag) return null;
              if (typeof tag === 'string') {
                return (
                  <span key={tag + i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                    #{tag}
                  </span>
                );
              }
              if (typeof tag === 'object' && typeof tag.name === 'string') {
                return (
                  <span key={tag.id ?? i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                    #{tag.name}
                  </span>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}
    </article>
  );
};

export default ArticleContent; 